"use client";

import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import languages from "@/data/languages.json";
import { motion } from "framer-motion";
import { ChevronDown, Mic } from "lucide-react";
import { useState } from "react";

type ConfigurableDemoEntryProps = {
  instructionLanguageCode: string;
  onInstructionLanguageChange: (code: string) => void;
  onStart: () => void;
  onTargetLanguageChange: (code: string) => void;
  pending: boolean;
  primaryButtonClassName: string;
  primaryButtonHoverClassName: string;
  targetLanguageCode: string;
};

const languageNamesByCode = new Map(Object.entries(languages).map(([name, code]) => [code, name]));

export function ConfigurableDemoEntry({
  instructionLanguageCode,
  onInstructionLanguageChange,
  onStart,
  onTargetLanguageChange,
  pending,
  primaryButtonClassName,
  primaryButtonHoverClassName,
  targetLanguageCode,
}: ConfigurableDemoEntryProps) {
  const [targetPickerOpen, setTargetPickerOpen] = useState(false);
  const [instructionPickerOpen, setInstructionPickerOpen] = useState(false);
  const [targetSearchTerm, setTargetSearchTerm] = useState("");
  const [instructionSearchTerm, setInstructionSearchTerm] = useState("");
  const targetLanguageName = languageNamesByCode.get(targetLanguageCode) || "";
  const instructionLanguageName = languageNamesByCode.get(instructionLanguageCode) || "English";
  const relationshipLabel =
    targetLanguageCode && targetLanguageCode === instructionLanguageCode
      ? `Conversation in ${instructionLanguageName}`
      : `Guided from ${instructionLanguageName}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex w-full max-w-3xl flex-col items-center rounded-2xl border border-white/10 bg-black/40 px-5 py-7 text-center shadow-2xl shadow-black/30 backdrop-blur-sm sm:px-9 sm:py-9"
      data-pre-session-variant="configurable-start"
    >
      <span className="rounded-full border border-orange-300/25 bg-orange-400/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-orange-100/80">
        Live speaking demo
      </span>
      <h1 className="mt-4 max-w-2xl text-3xl font-black leading-tight text-white sm:text-4xl">
        Try ChickyTutor in any language
      </h1>
      <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-300 sm:text-base">
        Choose what you want to practise and the language you want your tutor to use for guidance.
      </p>

      <div className="mt-6 w-full">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/55">
          I want to practise
        </p>
        <ResponsiveLanguagePicker
          open={targetPickerOpen}
          onOpenChange={setTargetPickerOpen}
          trigger={
            <button
              type="button"
              className="mx-auto mt-3 flex min-h-12 w-full max-w-md items-center justify-between gap-3 rounded-lg border border-white/15 bg-white/[0.05] px-4 py-3 text-left text-sm font-bold text-white transition-colors hover:border-orange-300/40"
              aria-label={`Choose a practice language${targetLanguageName ? `, currently ${targetLanguageName}` : ""}`}
            >
              {targetLanguageName || "Choose from all languages"}
              <ChevronDown className="h-4 w-4 text-white/50" aria-hidden="true" />
            </button>
          }
          languages={languages}
          selectedLanguageCode={targetLanguageCode}
          onSelectLanguage={onTargetLanguageChange}
          searchTerm={targetSearchTerm}
          onSearchTermChange={setTargetSearchTerm}
          title="Choose a practice language"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-sm text-white/60">
        <span>Beginner practice</span>
        <span aria-hidden="true">·</span>
        <ResponsiveLanguagePicker
          open={instructionPickerOpen}
          onOpenChange={setInstructionPickerOpen}
          trigger={
            <button
              type="button"
              className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.05] px-3 py-1.5 font-semibold text-white transition-colors hover:border-orange-300/40"
              aria-label={`Change instruction language, currently ${instructionLanguageName}`}
            >
              {relationshipLabel}
              <ChevronDown className="h-4 w-4 text-white/50" aria-hidden="true" />
            </button>
          }
          languages={languages}
          selectedLanguageCode={instructionLanguageCode}
          onSelectLanguage={onInstructionLanguageChange}
          searchTerm={instructionSearchTerm}
          onSearchTermChange={setInstructionSearchTerm}
          title="Choose an instruction language"
        />
      </div>

      <motion.button
        type="button"
        onClick={onStart}
        disabled={!targetLanguageCode || pending}
        className={`group relative mt-7 min-h-16 w-full max-w-md overflow-hidden rounded-lg px-6 py-4 transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${primaryButtonClassName} ${primaryButtonHoverClassName}`}
        whileHover={!targetLanguageCode || pending ? undefined : { y: -2 }}
        whileTap={!targetLanguageCode || pending ? undefined : { scale: 0.98 }}
      >
        <div className="absolute inset-0 translate-y-full bg-white/20 transition-transform duration-300 group-hover:translate-y-0" />
        <span className="relative flex items-center justify-center gap-3 text-lg font-black text-inherit sm:text-xl">
          <Mic className={pending ? "animate-pulse" : ""} size={23} />
          {pending ? `Preparing ${targetLanguageName} practice...` : "Start Speaking Practice"}
        </span>
      </motion.button>
      <p className="mt-3 text-xs font-medium text-white/40">
        Nothing starts until you press Start. Your browser may then ask for microphone access.
      </p>
    </motion.div>
  );
}
